import * as React from "react"
import { cn } from '@/lib/utils'
import { Badge } from "@/components/ui/badge"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> { }

export function Sidebar({ className, ...props }: SidebarProps) {
  return (
    <div className={cn("pb-12", className)} {...props} />
  )
}

interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> { }

export function SidebarHeader({
  className,
  ...props
}: SidebarHeaderProps) {
  return (
    <div
      className={cn("flex h-14 items-center border-b px-4", className)}
      {...props}
    />
  )
}

interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> { }

export function SidebarContent({
  className,
  ...props
}: SidebarContentProps) {
  return (
    <div className={cn("px-4 py-2", className)} {...props} />
  )
}

interface SidebarNavProps extends React.HTMLAttributes<HTMLDivElement> { }

export function SidebarNav({
  className,
  ...props
}: SidebarNavProps) {
  return (
    <nav className={cn("grid gap-1", className)} {...props} />
  )
}

interface SidebarNavHeaderProps extends React.HTMLAttributes<HTMLDivElement> { }

export function SidebarNavHeader({
  className,
  ...props
}: SidebarNavHeaderProps) {
  return (
    <div
      className={cn("flex items-center px-4 py-2", className)}
      {...props}
    />
  )
}

export interface SidebarNavLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  as?: React.ElementType;
  to?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  total?: number;
  newCount?: number;
}

export function SidebarNavLink({
  className,
  active,
  as: Component = 'a',
  icon,
  iconClassName,
  total,
  newCount,
  children,
  ...props
}: SidebarNavLinkProps) {
  return (
    <Component
      className={cn(
        "flex gap-2 items-center p-2 bg-transparent text-black/91 text-sm border border-transparent rounded-lg hover:border-royal-glow-4 hover:bg-royal-glow-4/80 focus-visible:border-royal-glow-4 focus-visible:ring-2 focus-visible:ring-custom-outline/50 focus-visible:outline-none disabled:opacity-50 focus-visible:disabled:opacity-60",
        active &&
        "border-royal-glow-4 bg-royal-glow-5/80 text-accent-foreground",
        className
      )}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            "shrink-0 [&>svg]:size-4 text-current",
            iconClassName
          )}
        >
          {icon}
        </span>
      )}
      {children}
      {(total !== undefined || (newCount !== undefined && newCount > 0)) && (
        <span className="flex gap-1.5 items-center ml-auto shrink-0">
          {total !== undefined && (
            <Badge variant="new_default">{total}</Badge>
          )}
          {newCount !== undefined && newCount > 0 && (
            <Badge className="rounded-md" variant="number_default">{newCount}</Badge>
          )}
        </span>
      )}
    </Component>
  )
}

interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> { }

export function SidebarFooter({
  className,
  ...props
}: SidebarFooterProps) {
  return (
    <div
      className={cn("mt-auto border-t px-4 py-4", className)}
      {...props}
    />
  )
}